<template>
  <div class="capability-picker" v-loading="loading">
    <div v-for="capability in catalog" :key="capability.capabilityId" class="capability-card">
      <el-checkbox :value="isSelected(capability.capabilityId)" @change="toggle(capability)">
        {{ capability.name }}
      </el-checkbox>
      <div class="capability-description">{{ capability.description }}</div>
      <div v-if="isSelected(capability.capabilityId)" class="capability-options">
        <template v-if="capability.capabilityId === 'commerce.product.search'">
          <div class="capability-field">
            <div class="capability-field-label"><span class="required-mark">*</span>商城连接</div>
            <el-select :value="selectedStore(capability.capabilityId)" placeholder="请选择商城连接"
              style="width: 100%"
              @visible-change="visible => visible && requestOptions(capability.capabilityId)"
              @change="value => updateOptions(capability.capabilityId, value)">
              <el-option v-for="option in optionsById[capability.capabilityId] || []" :key="option.value"
                :label="option.label" :value="option.value" />
            </el-select>
          </div>
          <div v-for="field in textFields(capability)" :key="field.key" class="capability-field">
            <div class="capability-field-label">
              <span v-if="field.required" class="required-mark">*</span>{{ field.label }}
              <span class="field-key">{{ field.key }}</span>
            </div>
            <el-input :value="configValue(capability.capabilityId, field.key)"
              :placeholder="field.key === 'tuanzhangId' ? '选填，例如 1' : `请输入${field.label}`"
              @input="value => updateConfigValue(capability.capabilityId, field.key, value)" />
          </div>
        </template>
        <el-select v-else-if="capability.capabilityId === 'knowledge.dify.search'" :value="selectedKnowledge(capability.capabilityId)" multiple
          placeholder="请选择知识库（可多选）" style="width: 100%"
          @visible-change="visible => visible && requestOptions(capability.capabilityId)"
          @change="value => updateOptions(capability.capabilityId, value)">
          <el-option v-for="option in optionsById[capability.capabilityId] || []" :key="option.value"
            :label="option.label" :value="option.value" />
        </el-select>
      </div>
    </div>
    <div v-for="capability in unavailableSelected" :key="`unavailable-${capability.capabilityId}`"
      class="capability-card unavailable-card">
      <strong>{{ capability.name || capability.capabilityId }}</strong>
      <el-tag type="warning" size="mini">已下线</el-tag>
      <div class="capability-description">这项 Skill 暂不可调用；原配置会保留，除非你主动移除。</div>
      <el-button size="mini" @click="removeUnavailable(capability.capabilityId)">移除绑定</el-button>
    </div>
    <div v-if="!loading && !catalog.length" class="empty-tip">
      暂无可用 Skill，已保存配置会继续保留。
    </div>
  </div>
</template>

<script>
import {
  capabilityNeedsOptions,
  setCapabilityConfigValue,
  setCapabilityOptions,
  toggleCapabilityBinding
} from '@/utils/agent-capability-config.mjs'

export default {
  name: 'AgentCapabilityPicker',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    catalog: {
      type: Array,
      default: () => []
    },
    unavailableSelected: {
      type: Array,
      default: () => []
    },
    optionsById: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    bindingFor(capabilityId) {
      return this.value.find(item => item.capabilityId === capabilityId)
    },
    isSelected(capabilityId) {
      return Boolean(this.bindingFor(capabilityId))
    },
    selectedStore(capabilityId) {
      return this.bindingFor(capabilityId)?.config?.storeGuid || ''
    },
    selectedKnowledge(capabilityId) {
      return this.bindingFor(capabilityId)?.config?.knowledgeBaseIds || []
    },
    configValue(capabilityId, key) {
      return this.bindingFor(capabilityId)?.config?.[key] || ''
    },
    textFields(capability) {
      return (capability.configFields || []).filter(field => field.control === 'text')
    },
    toggle(capability) {
      const next = toggleCapabilityBinding(this.value, capability)
      this.$emit('input', next)
      if (capabilityNeedsOptions(capability.capabilityId)
        && next.some(item => item.capabilityId === capability.capabilityId)) {
        this.requestOptions(capability.capabilityId)
      }
    },
    removeUnavailable(capabilityId) {
      // 只在用户明确点击时删除旧绑定，目录刷新或总开关变化不应改写表单配置。
      this.$emit('input', this.value.filter(item => item.capabilityId !== capabilityId))
    },
    updateOptions(capabilityId, value) {
      const option = (this.optionsById[capabilityId] || [])
        .find(item => String(item.value) === String(value))
      this.$emit('input', setCapabilityOptions(this.value, capabilityId, value, option?.defaults))
    },
    updateConfigValue(capabilityId, key, value) {
      this.$emit('input', setCapabilityConfigValue(this.value, capabilityId, key, value))
    },
    requestOptions(capabilityId) {
      this.$emit('request-options', capabilityId)
    }
  }
}
</script>

<style lang="scss" scoped>
.capability-card {
  margin-bottom: 10px;
  padding: 12px;
  background: #fafafa;
  border: 1px solid #ebeef5;
  border-radius: 6px;
}

.unavailable-card .el-tag { margin-left: 8px; }
.unavailable-card .el-button { margin-top: 8px; }

.capability-description,
.empty-tip {
  margin-top: 5px;
  color: #909399;
  font-size: 12px;
  line-height: 1.5;
}

.capability-options {
  margin-top: 10px;
}

.capability-field + .capability-field {
  margin-top: 10px;
}

.capability-field-label {
  margin-bottom: 5px;
  color: #606266;
  font-size: 13px;
}

.required-mark {
  margin-right: 3px;
  color: #f56c6c;
}

.field-key {
  margin-left: 6px;
  color: #c0c4cc;
  font-size: 12px;
}
</style>
